import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import {
  InputAdornment,
  Autocomplete as MuiAutocomplete,
  type SxProps,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box } from '@ovComponents/1-primative/box/box';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useThemeTokens } from '@providers/themeTokenProvider';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpText } from '../helpText/helpText';
import { useHelpTextTokens } from '../helpText/helpText.tokens';
import InfoDialog from '../infoDialog/infoDialog';
import { useTextFieldTokens } from '../textField/textField.tokens';
import { Tooltip } from '../tooltip/tooltip';

export type AutocompleteProps = {
  label: string;
  open?: boolean;
  helpText?: string;
  error?: boolean;
  errorText?: string;
  success?: boolean;
  successText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  options: any[];
  isOptionEqualToValue?: any;
  value?: any;
  onChange?: any;
  onInputChange?: any;
  onFocus?: any;
  onClick?: any;
  onBlur?: any;
  multiple?: boolean;
  freeSolo?: boolean;
  renderTags?: any;
  renderOption?: any;
  sx?: SxProps;
  labelSx?: SxProps;
  InputProps?: object;
  ChipProps?: object;
  getOptionLabel?: (option: any) => string;
  width?: string;
  componentsProps?: object;
  inputReadOnly?: boolean;
  filterOptions?: (options: any[], state: object) => any[];
  loading?: boolean;
  autoFocus?: boolean;
  blurOnSelect?: boolean;
  disableClearable?: boolean;
  infoTooltip?: string;
  size?: 'small' | 'medium';
  testId?: string;
  lockMessage?: string;
  locked?: boolean;
  disableCloseOnSelect?: boolean;
  showSearchIcon?: boolean;
  placeholder?: string;
  required?: boolean;
};

const StyledTextField = styled(TextField)(() => {
  const tokens = useThemeTokens(useTextFieldTokens());
  const { comp } = tokens;

  return {
    '& .MuiOutlinedInput-root': {
      width: '100%',
      paddingLeft: '12px',
      paddingRight: '14px !important',
      '& input': {
        padding: '0px !important',
        color: comp.textField.textColor,
        fontSize: comp.textField.fontSize,
        fontWeight: comp.textField.fontWeight,
      },
      '& fieldset': {
        borderColor: comp.textField.borderColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: comp.textField.focusBorderColor,
      },
      '& .MuiSelect-select': {
        paddingLeft: '0px',
        marginLeft: '12px',
        marginRight: '12px',
      },
      '& .MuiInputAdornment-positionEnd': {
        marginLeft: '0px',
      },
      '&.Mui-error': {
        '& input': {
          color: comp.textField.errorColor,
        },
        '& fieldset': {
          borderColor: comp.textField.errorColor,
        },
      },
    },
  };
});

export const Autocomplete = ({
  error = false,
  label,
  open,
  options,
  fullWidth,
  errorText,
  helpText,
  successText,
  success = false,
  isOptionEqualToValue,
  value,
  onChange,
  onFocus,
  onClick,
  multiple,
  renderTags,
  renderOption,
  onInputChange,
  disabled,
  InputProps,
  ChipProps,
  getOptionLabel,
  width,
  sx,
  onBlur,
  componentsProps,
  filterOptions,
  autoFocus,
  inputReadOnly = false,
  freeSolo = false,
  blurOnSelect = false,
  infoTooltip,
  size,
  testId,
  lockMessage,
  locked,
  disableCloseOnSelect,
  disableClearable,
  showSearchIcon = false,
  placeholder,
  required,
}: AutocompleteProps) => {
  const variantStyle: CSSProperties = {};
  const { sys, comp } = useThemeTokens(useTextFieldTokens());
  const { comp: helpTextComp } = useThemeTokens(useHelpTextTokens());
  const { t } = useTranslation('components');

  const style: CSSProperties = {
    ...variantStyle,
    height: multiple ? 'fit-content' : '40px',
    color: comp.textField.textColor,
    fontSize: comp?.textField?.fontSize ?? '16px',
    fontWeight: comp.textField.fontWeight,
    fontFamily: sys.font.base,
    borderRadius: '4px',
    width: fullWidth ? '100%' : width || 'auto',
    background: '#ffffff',
  };

  const shouldDisplayInDialog = helpTextComp.helpText.displayInDialog;
  const helpTextToShow =
    infoTooltip && !shouldDisplayInDialog ? infoTooltip : helpText;

  const helpTextFunc = (isError: boolean, isSuccess: boolean) => {
    if (isError) {
      return (
        (errorText ?? t('fieldErrors.default', { fieldName: label })) ||
        helpTextToShow
      );
    }

    if (isSuccess) {
      return successText || helpTextToShow;
    }

    return helpTextToShow;
  };

  return (
    <Box sx={{ ...sx }}>
      {label && (
        <Box display='flex' flexDirection='row' alignItems={'center'}>
          <Typography
            variant='labelSmall'
            sx={{
              mb: '4px',
              color: error
                ? comp.textField.errorColor
                : comp.textField.labelColor,
            }}
          >
            {label}
            {required && (
              <span style={{ color: comp.textField.requiredIndicatorColor }}>
                *
              </span>
            )}
            {infoTooltip && shouldDisplayInDialog && (
              <InfoDialog information={infoTooltip} />
            )}
          </Typography>
        </Box>
      )}
      <MuiAutocomplete
        data-testid={testId ?? 'auto-complete'}
        renderInput={(params: any) => (
          <StyledTextField
            ref={params?.InputProps.ref}
            {...params}
            error={!!error}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              ...InputProps,
              ...(showSearchIcon
                ? {
                    startAdornment: (
                      <>
                        <InputAdornment position='start'>
                          <SearchIcon />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }
                : {}),
              ...(locked
                ? {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <Tooltip title={lockMessage ?? ''}>
                          <Icon icon={LockIcon} size='small' />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }
                : {}),
            }}
            style={style}
            inputProps={{
              ...params.inputProps,
              disabled: inputReadOnly || disabled || locked,
              'data-testid': `${testId ?? 'auto-complete'}-input`,
            }}
            autoFocus={autoFocus}
          />
        )}
        open={open}
        // disablePortal
        sx={{
          '& + .MuiAutocomplete-popper .MuiAutocomplete-option': {
            color: comp.textField.textColor,
            fontSize: comp.textField.fontSize,
            fontWeight: comp.textField.fontWeight,
          },
        }}
        freeSolo={freeSolo}
        options={options}
        isOptionEqualToValue={isOptionEqualToValue}
        value={value}
        onChange={onChange}
        onInputChange={onInputChange}
        onFocus={onFocus}
        onClick={onClick}
        onBlur={onBlur}
        fullWidth={fullWidth}
        style={style}
        multiple={multiple}
        renderTags={renderTags}
        getOptionLabel={getOptionLabel}
        componentsProps={componentsProps}
        filterOptions={filterOptions}
        openOnFocus
        blurOnSelect={blurOnSelect}
        renderOption={renderOption}
        size={size}
        disabled={disabled || locked}
        disableCloseOnSelect={disableCloseOnSelect}
        ChipProps={ChipProps}
        disableClearable={disableClearable}
      />
      {helpTextFunc(error, success) && (
        <HelpText
          text={helpTextFunc(error, success)}
          tone={error ? 'error' : success ? 'success' : 'neutral'}
        />
      )}
    </Box>
  );
};
